import { useCallback, useRef, useState } from 'react';
import { useReverification, useUser } from '@clerk/react';
import type { PhoneNumberResource } from '@clerk/types';
import {
  SUPPORTED_COUNTRIES,
  type SupportedCountryCode,
  clerkMessage,
  formatByCountry,
  stripDigits,
  validateForCountry,
} from '@client/util/clerkAuthHelpers';

export type ClerkPhoneVerificationStep = 'phone' | 'verify';

export function getPhoneVerificationErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const clerkErrors = 'errors' in error ? error.errors : undefined;
    if (Array.isArray(clerkErrors) && clerkErrors.length > 0) {
      const first = clerkErrors[0] as { longMessage?: string; message?: string };
      return clerkMessage(first) ?? 'We could not complete that step. Please try again.';
    }

    if ('message' in error && typeof error.message === 'string') return error.message;
  }

  return 'We could not complete that step. Please try again.';
}

export function useClerkPhoneVerification({
  onVerified,
  onStepChange,
}: {
  onVerified: (phone: PhoneNumberResource) => Promise<void>;
  onStepChange?: (step: ClerkPhoneVerificationStep) => void;
}) {
  const { user } = useUser();
  const [step, setStep] = useState<ClerkPhoneVerificationStep>('phone');
  const [countryCode, setCountryCode] = useState<SupportedCountryCode>(
    SUPPORTED_COUNTRIES[0]?.code ?? '+1',
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const phoneResourceRef = useRef<PhoneNumberResource | null>(null);

  const createPhoneNumber = useReverification((e164: string) => {
    if (!user) throw new Error('Your signed-in account is not available. Please refresh the page.');
    return user.createPhoneNumber({ phoneNumber: e164 });
  });

  const transitionTo = useCallback(
    (nextStep: ClerkPhoneVerificationStep) => {
      setStep(nextStep);
      onStepChange?.(nextStep);
    },
    [onStepChange],
  );

  const updatePhoneNumber = useCallback(
    (value: string) => {
      setPhoneNumber(formatByCountry(stripDigits(value), countryCode));
      setError(null);
    },
    [countryCode],
  );

  const updateCountryCode = useCallback((value: SupportedCountryCode) => {
    setCountryCode(value);
    setPhoneNumber('');
    setError(null);
  }, []);

  const sendPhoneCode = useCallback(async () => {
    if (!user) return;

    const digits = stripDigits(phoneNumber);
    const validationError = validateForCountry(digits, countryCode);
    if (validationError) {
      setError(validationError);
      return;
    }

    const e164 = `${countryCode}${digits}`;
    setIsSubmitting(true);
    setError(null);
    try {
      let phoneResource = user.phoneNumbers.find(phone => phone.phoneNumber === e164) ?? null;

      if (phoneResource?.verification.status === 'verified') {
        await onVerified(phoneResource);
        return;
      }

      if (!phoneResource) {
        phoneResource = (await createPhoneNumber(e164)) ?? null;
        await user.reload();
      }

      if (!phoneResource) throw new Error('We could not add this phone number. Please try again.');

      phoneResourceRef.current = phoneResource;
      await phoneResource.prepareVerification();
      setPhoneCode('');
      transitionTo('verify');
    } catch (requestError) {
      setError(getPhoneVerificationErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }, [countryCode, createPhoneNumber, onVerified, phoneNumber, transitionTo, user]);

  const verifyPhoneCode = useCallback(async () => {
    const phoneResource = phoneResourceRef.current;
    if (!phoneResource || !user) {
      setError('Your phone verification expired. Please enter your number again.');
      transitionTo('phone');
      return;
    }

    if (phoneCode.length !== 6) {
      setError('Enter the 6-digit code sent to your phone.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const verifiedPhone = await phoneResource.attemptVerification({ code: phoneCode });
      if (verifiedPhone.verification.status !== 'verified') {
        throw new Error('That code could not be verified. Please try again.');
      }
      await user.reload();
      await onVerified(verifiedPhone);
    } catch (requestError) {
      setError(getPhoneVerificationErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }, [onVerified, phoneCode, transitionTo, user]);

  const resendPhoneCode = useCallback(async () => {
    if (!phoneResourceRef.current) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await phoneResourceRef.current.prepareVerification();
      setPhoneCode('');
    } catch (requestError) {
      setError(getPhoneVerificationErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const returnToPhone = useCallback(() => {
    setPhoneCode('');
    setError(null);
    transitionTo('phone');
  }, [transitionTo]);

  return {
    step,
    countryCode,
    phoneNumber,
    phoneCode,
    error,
    isSubmitting,
    updatePhoneNumber,
    updateCountryCode,
    sendPhoneCode,
    setPhoneCode,
    verifyPhoneCode,
    resendPhoneCode,
    returnToPhone,
  };
}
